import { Box, Chip, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useGetRecurringJobsQuery } from '../../../../app/Slices/SevenDayApiSlice';
import { Job } from '../../../../app/Slices/SevenDayApiTypes';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import { useSearchParams } from 'react-router-dom';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { MRT_ColumnDef } from 'mantine-react-table';
import GenericTable from '../../../shared/GenericTable';
import ContractorRecurringModal from '../../Reservations/ReservationContractors/ContractorRecurringModal';

interface JobListProps {
    enabled?: boolean;
    id?: number;
}

const RecurringList = ({ enabled }: JobListProps) => {
    const { data: jobs, isFetching } = useGetRecurringJobsQuery({
        enabled: enabled,
    });
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [selectedJob, setSelectedJob] = useState<number | null>(null);
    const [jobOpen, setJobOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const filteredJobs = React.useMemo(() => {
        if (!jobs) return [];
        if (enabled === true) return jobs.filter((j) => j.enabled === true);
        if (enabled === false) return jobs.filter((j) => j.enabled === false);
        return jobs;
    }, [jobs, enabled]);

    useEffect(() => {
        const id = searchParams.get('selectedJob');
        if (id) {
            const jobId = parseInt(id, 10);
            setSelectedJob(jobId);
            if (filteredJobs.length) {
                const idx = filteredJobs.findIndex((j) => j.id === jobId);
                setCurrentIndex(idx >= 0 ? idx : null);
            }
        }
    }, [searchParams, filteredJobs]);

    const handleRowClick = (row: any) => {
        const idx = filteredJobs.findIndex((j) => j.id === row?.id);
        setCurrentIndex(idx >= 0 ? idx : null);
        setSelectedJob(row?.id ?? null);

        const next = new URLSearchParams(searchParams);
        if (row?.id) next.set('selectedJob', String(row.id));
        setSearchParams(next, { replace: true });

        setJobOpen(true);
    };

    const handleClose = () => {
        setJobOpen(false);
        setSelectedJob(null);
        const next = new URLSearchParams(searchParams);
        next.delete('selectedJob');
        setSearchParams(next, { replace: true });
    };

    const handleNavigate = (dir: 'prev' | 'next') => {
        if (currentIndex == null || !filteredJobs.length) return;
        const last = filteredJobs.length - 1;

        const nextIndex =
            dir === 'next'
                ? Math.min(currentIndex + 1, last)
                : Math.max(currentIndex - 1, 0);

        if (nextIndex === currentIndex) return;

        setCurrentIndex(nextIndex);
        const job = filteredJobs[nextIndex];
        setSelectedJob(job?.id ?? null);

        const next = new URLSearchParams(searchParams);
        if (job?.id) next.set('selectedJob', String(job.id));
        else next.delete('selectedJob');
        setSearchParams(next, { replace: true });
    };

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'id',
            header: 'Job Id',
            accessorFn: (row) => `#${row.id}`,
        },
        {
            accessorKey: 'contractor.name',
            header: 'Contractor',
            accessorFn: (row) => row?.contractor?.name,
        },
        {
            accessorKey: 'jobDay',
            header: 'Scheduled For',
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return `${camelCaseToTitleCase(value)}`;
            },
        },
        {
            accessorKey: 'modules',
            header: 'Experiences',
            Cell: ({ cell }) => {
                const modules = (cell.getValue<any[]>() ?? []).filter(Boolean);

                const displayed = modules.slice(0, isMobile ? 1 : 2);
                const remainder = Math.max(
                    0,
                    modules.length - displayed.length,
                );

                const getName = (m: any) => {
                    if (m && typeof m === 'object')
                        return m.name ?? m.label ?? String(m.id ?? '');
                    if (typeof m === 'string') return camelCaseToTitleCase(m);
                    if (typeof m === 'number') return String(m);
                    return '';
                };

                return (
                    <Box sx={{ display: 'flex', overflow: 'clip', gap: 0.5 }}>
                        {displayed.map((m: any, idx: number) => (
                            <CustomChip
                                key={m?.id ?? idx}
                                variant='filled'
                                sx={{
                                    backgroundColor: 'lightgrey',
                                }}
                                label={getName(m)}
                            />
                        ))}
                        {remainder > 0 && (
                            <Chip
                                sx={{ height: 'auto', fontSize: '10px' }}
                                label={`+ ${remainder}`}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            accessorKey: 'services',
            header: 'Services',
            Cell: ({ cell }) => {
                const value = cell.getValue<any[]>();

                const remainders = isMobile ? value.length : value.length - 2;

                return (
                    <Box sx={{ display: 'flex', overflow: 'clip', gap: 0.5 }}>
                        {!isMobile &&
                            value
                                ?.slice(0, 2)
                                .map((service: string, index: number) => (
                                    <CustomChip
                                        key={index}
                                        variant='filled'
                                        sx={{
                                            backgroundColor: 'lightgrey',
                                        }}
                                        label={camelCaseToTitleCase(service)}
                                    />
                                ))}
                        {remainders > 0 && (
                            <Chip
                                sx={{ height: 'auto', fontSize: '10px' }}
                                label={`+ ${remainders}`}
                            />
                        )}
                    </Box>
                );
            },
        },

        {
            accessorKey: 'enabled',
            header: 'Status',
            Cell: ({ cell }) => {
                const value = cell.getValue<any>();

                return (
                    <CustomChip
                        variant='outlined'
                        color={value === true ? 'blue' : 'red'}
                        label={value ? 'Enabled' : 'Disabled'}
                    />
                );
            },
        },
    ];

    return (
        <Box sx={{ width: '100%', paddingTop: '20px' }}>
            <GenericTable
                columns={columns}
                data={filteredJobs}
                tableName='Recurring-Job-List'
                loading={!jobs}
                onRowClick={handleRowClick}
                cursor={'pointer'}
            />
            <ContractorRecurringModal
                id={selectedJob}
                jobs={filteredJobs}
                isEditing={true}
                open={jobOpen}
                onClose={handleClose}
                index={currentIndex}
                onNavigate={handleNavigate}
            />
        </Box>
    );
};

export default RecurringList;
